import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, errorMessage, setToken, getToken } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, object = user
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(false);
      setChecking(false);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setToken(null);
      setUser(false);
      return null;
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) setToken(data.access_token);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: errorMessage(e) };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data.access_token) setToken(data.access_token);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: errorMessage(e) };
    }
  };

  const resetPassword = async (token, password) => {
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      if (data.access_token) setToken(data.access_token);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: errorMessage(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("Logout request failed (clearing session anyway):", e);
    }
    setToken(null);
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, checking, login, register, logout, refresh, resetPassword }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
